<!-- The list view: the same wall as a sectioned list, grouped by the current
     Group by, filtered by the same filters. The parity path for screen readers:
     every coffee is one tap from its full page. Each row shows the same bag
     front the wall does (BagFront). -->
<template>
  <IonList class="shelf-list" lines="none">
    <IonItemGroup v-for="b in sections" :key="b.key">
      <IonItemDivider sticky class="shelf-list__head">
        <IonLabel>
          <span class="head__title">{{ b.title }}</span>
          <span class="head__count">{{ b.rows.length }} {{ b.rows.length === 1 ? 'coffee' : 'coffees' }}</span>
        </IonLabel>
      </IonItemDivider>
      <IonItem
        v-for="c in b.rows"
        :key="c.slug"
        button
        :detail="false"
        class="row"
        @click="open(c)"
      >
        <div slot="start" class="thumb">
          <BagFront :coffee="c" variant="thumb" class="thumb__bag" />
        </div>
        <IonLabel class="row__text">
          <h3>{{ shortName(c, 48) }}</h3>
          <p>{{ c.roaster }}<template v-if="origin(c)"> · {{ origin(c) }}</template></p>
          <span v-if="flavourById[c.flavour]" class="flavour">
            <i :style="{ background: flavourById[c.flavour].colour }" aria-hidden="true" />
            {{ flavourById[c.flavour].short }}
          </span>
        </IonLabel>
        <div slot="end" class="price">
          <span v-if="!c.inStock" class="sold">Sold out</span>
          <template v-else-if="c.fromPrice != null">
            <span class="from">from</span>
            <strong>${{ price(c.fromPrice) }}</strong>
          </template>
        </div>
      </IonItem>
    </IonItemGroup>
  </IonList>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { IonList, IonItemGroup, IonItemDivider, IonItem, IonLabel } from '@ionic/vue';
import { flavourById, matchingSlugs } from '../store.js';
import { shortName } from '../lib/wall-look.js';
import BagFront from './BagFront.vue';

const props = defineProps({ bays: { type: Array, required: true } });

const router = useRouter();

const sections = computed(() => props.bays
  .map((b) => ({ ...b, rows: b.coffees.filter((c) => matchingSlugs.value.has(c.slug)) }))
  .filter((b) => b.rows.length));

const origin = (c) => (c.origins?.length > 1 ? 'Multi-origin' : c.origins?.[0] || '');
const price = (p) => (Number.isInteger(p) ? p : p.toFixed(2));
const open = (c) => router.push(`/tabs/shelf/coffee/${c.slug}`);
</script>

<style scoped>
.shelf-list { background: transparent; padding: 0 0 16px; }
.shelf-list__head {
  --background: var(--paper, #F4EFE6);
  --color: var(--ink, #1F2A22);
  --padding-start: 16px;
  min-height: 48px;
  border-bottom: 0;
}
.shelf-list__head ion-label { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin: 0; padding-right: 16px; }
.head__title {
  font-family: var(--font-display, 'Bricolage Grotesque', sans-serif);
  font-size: var(--text-xl);
  font-weight: 800;
  letter-spacing: -0.02em;
}
.head__count { font-family: var(--font-mono, 'Martian Mono', monospace); font-size: 11px; font-weight: 500; color: var(--muted, #5E6B60); }
.row {
  --background: var(--dock, #FAF7F1);
  --padding-start: 12px;
  --inner-padding-end: 14px;
  --min-height: 84px;
  --border-radius: var(--radius, 16px);
  margin: 0 12px 8px;
}
.thumb {
  width: 60px;
  height: 68px;
  margin: 8px 12px 8px 0;
  display: grid;
  place-items: end center;
  background: var(--paper, #F4EFE6);
  border-radius: 12px;
  padding: 6px 0 5px;
}
.thumb .thumb__bag { width: auto; height: 100%; }
.row__text h3 {
  font-family: var(--font-body, 'Manrope', sans-serif);
  font-size: var(--text-md);
  font-weight: 700;
  color: var(--ink, #1F2A22);
  margin: 0 0 2px;
  white-space: normal;
}
.row__text p { font-size: 13px; font-weight: 500; color: var(--muted, #5E6B60); margin: 0; }
.flavour {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  padding: 3px 9px 3px 7px;
  border-radius: var(--radius-pill, 999px);
  background: var(--sage, #E6EDE4);
  color: var(--ink-2, #3E4A41);
  font-size: var(--text-xs);
  font-weight: 700;
}
.flavour i { width: 7px; height: 7px; border-radius: 50%; }
.price { display: grid; justify-items: end; gap: 1px; white-space: nowrap; font-family: var(--font-mono, 'Martian Mono', monospace); }
.price strong { font-weight: 500; font-size: 14px; color: var(--ink, #1F2A22); font-variant-numeric: tabular-nums; }
.from { font-size: 10px; color: var(--muted, #5E6B60); }
.sold { font-size: 10px; font-weight: 500; color: var(--muted, #5E6B60); text-transform: uppercase; letter-spacing: 0.04em; }
</style>
